/**
 * Size-target arithmetic, kept apart from the planner so the size
 * calculator on Wave's pages can load it without the rest of the engine.
 * Used by `planMp4` in plan.ts and by src/explainers.ts.
 */

/** Highest short edge that still looks reasonable at a given H.264 bitrate.
 *  Below these thresholds the encoder smears motion into blocks, and a
 *  smaller frame with the same bits looks better. Scaled to 30 fps. */
export function shortEdgeForBitrate(videoBps: number, fps: number): number | null {
  const at30 = videoBps * (30 / Math.max(24, fps));
  if (at30 < 350_000) return 360;
  if (at30 < 800_000) return 480;
  if (at30 < 1_600_000) return 720;
  if (at30 < 3_500_000) return 1080;
  if (at30 < 8_000_000) return 1440;
  return null;
}

export interface TargetBudget {
  /** Everything the size allows per second, after the 5% reserve. */
  totalBps: number;
  audioKbps: number;
  /** Video bitrate, floored at the minimum Wave will encode. */
  videoBps: number;
  /** Short-edge cap the bitrate calls for, or null to keep the source size. */
  shortEdge: number | null;
  /** True when the video share would fall under the minimum. */
  blocked: boolean;
  /** Smallest size that clears the minimum for this length, in bytes. */
  minBytes: number;
}

/** Below this, H.264 video is unwatchable at any size Wave would pick. */
const MIN_TARGET_VIDEO_BPS = 120_000;

/** How a size target splits into audio and video for a given length. Shared
 *  by the planner and the size calculator on Wave's compress pages, so the
 *  page never promises something the planner won't do. */
export function targetBudget(targetBytes: number, seconds: number, fps = 30, keepAudio = true): TargetBudget {
  // 5% headroom covers the container and the encoder's tendency to
  // overshoot its requested rate by a few percent.
  const totalBps = (targetBytes * 8 * 0.95) / seconds;
  const audioKbps = keepAudio ? (totalBps < 600_000 ? 64 : totalBps < 1_500_000 ? 96 : 128) : 0;
  const rawVideoBps = totalBps - audioKbps * 1000;
  const videoBps = Math.max(MIN_TARGET_VIDEO_BPS, Math.round(rawVideoBps));
  return {
    totalBps,
    audioKbps,
    videoBps,
    shortEdge: shortEdgeForBitrate(videoBps, fps),
    blocked: rawVideoBps < MIN_TARGET_VIDEO_BPS,
    minBytes: (((MIN_TARGET_VIDEO_BPS + audioKbps * 1000) * seconds) / 8) / 0.95,
  };
}
